import { useEffect, useState } from 'react'
import { api, type BenchmarkOverview } from '../api'
import { RowMenu } from '../components/RowMenu'
import { SideNav } from '../components/SideNav'

/**
 * Benchmarks — proposal §4.1, §6.4; IMPLEMENTATION.md decisions 54 and 55. The Snapshot Evaluator pack.
 *
 * A limit written `BM +/- 1 year`, or `BM + 15%` on each country, is judged against the
 * benchmark's own figure for the holdings' day. **The benchmarks are the ones the funds'
 * guidelines name**, added as the guidelines are imported, and each fund is judged against
 * its own; so there is nothing to set up, and all anybody provides is figures. Two parts, down
 * the left as Settlement calendars lays its parts out: **Benchmarks**, each with the funds
 * naming it and its latest figures; **Figures**, the template, loading a file from the firm's
 * own index export, and the files loaded.
 */

type View = 'benchmarks' | 'figures'

const VIEWS: [View, string, string][] = [
  ['benchmarks', 'Benchmarks', 'The benchmarks the funds’ guidelines name, and their latest figures'],
  ['figures', 'Figures', 'Load each benchmark’s duration and weights, by day'],
]

function said(e: unknown): string {
  return e instanceof Error ? e.message : String(e)
}

function plural(n: number, one: string, many = `${one}s`): string {
  return `${n} ${n === 1 ? one : many}`
}

export function Benchmarks({ canLoad }: { canLoad: boolean }) {
  const [view, setView] = useState<View>('benchmarks')
  const [reads, setReads] = useState(0)
  const [data, setData] = useState<BenchmarkOverview | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [problems, setProblems] = useState<string[]>([])
  const [done, setDone] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [file, setFile] = useState<File | null>(null)

  useEffect(() => {
    let live = true
    api
      .benchmarks()
      .then((found) => live && setData(found))
      .catch((e: unknown) => live && setError(said(e)))
    return () => {
      live = false
    }
  }, [reads])

  async function load() {
    if (!file) return
    setBusy(true)
    setError(null)
    setProblems([])
    setDone(null)
    try {
      const loaded = await api.loadBenchmarkFigures(file)
      setFile(null)
      setDone(`${loaded.file.filename}: ${plural(loaded.rows, 'figure')} loaded for ${loaded.benchmarks.join(', ')}.`)
      setReads((n) => n + 1)
    } catch (e) {
      setError(said(e))
      setProblems((e as { problems?: string[] }).problems ?? [])
    } finally {
      setBusy(false)
    }
  }

  async function template(format: 'csv' | 'xlsx') {
    setError(null)
    try {
      await api.downloadBenchmarkTemplate(format)
    } catch (e) {
      setError(said(e))
    }
  }

  return (
    <section className="calendars benchmarks">
      <header className="page-head">
        <h2>Benchmarks</h2>
        <p className="note">
          The benchmarks your funds’ guidelines name, and their figures by day. A limit such as BM +/- 1 year is judged
          on its fund’s benchmark’s figures as at the holdings’ day.
        </p>
      </header>

      <div className="settings-layout">
        <SideNav
          label="Benchmarks"
          sections={[
            {
              title: 'Benchmarks',
              hint: 'What limits against the benchmark are judged on',
              entries: VIEWS.map(([id, label, blurb]) => ({
                key: id,
                label,
                blurb,
                showing: view === id,
                select: () => {
                  setView(id)
                  setDone(null)
                  setError(null)
                  setProblems([])
                },
              })),
            },
          ]}
        />
        <div className="settings-content">
          {error && (
            <p className="error" role="alert">
              {error}
            </p>
          )}
          {problems.length > 0 && (
            <ul className="hits" data-testid="benchmark-problems">
              {problems.map((p) => (
                <li key={p}>{p}</li>
              ))}
            </ul>
          )}
          {done && (
            <p className="done" role="status">
              {done}
            </p>
          )}
          {data === null && !error && <p className="loading">Loading…</p>}

          {data && view === 'benchmarks' && (
            <div className="calendars-part" data-testid="benchmarks-list-part">
              <h3>Benchmarks</h3>
              <p className="note">
                Each benchmark a fund’s guidelines name, as the guidelines write it, added when they are imported. A
                fund’s limits against the benchmark are judged on the figures of the benchmark its guidelines name.
              </p>
              {data.benchmarks.length === 0 ? (
                <p className="empty">No fund’s guidelines name a benchmark yet.</p>
              ) : (
                <div className="table-scroll">
                  <table className="grid" data-testid="benchmark-list">
                    <thead>
                      <tr>
                        <th>Benchmark</th>
                        <th>Funds whose guidelines name it</th>
                        <th>Latest figures</th>
                        <th className="num">Days loaded</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.benchmarks.map((b) => (
                        <tr key={b.id} data-testid="benchmark-row">
                          <td>
                            <strong>{b.name}</strong>
                          </td>
                          <td>{b.funds.join(', ') || '—'}</td>
                          <td>{b.latest_figures ?? <span className="clause">None loaded</span>}</td>
                          <td className="num">{b.days}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {data && view === 'figures' && (
            <div className="calendars-part" data-testid="benchmarks-figures">
              <h3>Figures</h3>
              <p className="note">
                Each benchmark’s duration, in years, and its weights by classification, each the percentage of the
                benchmark in a member, from your index provider’s export, with its rating band where a limit sets investment grade and high yield apart. The template lists every figure the funds’
                limits need for the latest holdings day. A later file for a benchmark and day replaces that day.
              </p>
              <p className="actions">
                <RowMenu
                  label="Download the template"
                  text="Template"
                  items={[
                    { label: 'CSV', onSelect: () => void template('csv') },
                    { label: 'Excel', onSelect: () => void template('xlsx') },
                  ]}
                />
              </p>
              {canLoad && (
                <div className="row-form">
                  <label>
                    Benchmark figures file
                    <input type="file" accept=".csv,.xlsx" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
                  </label>
                  <button type="button" disabled={busy || !file} onClick={() => void load()}>
                    Load
                  </button>
                </div>
              )}
              {data.files.length === 0 ? (
                <p className="empty">No figures loaded yet.</p>
              ) : (
                <div className="table-scroll">
                  <table className="grid" data-testid="benchmark-files">
                    <thead>
                      <tr>
                        <th>File</th>
                        <th>Figures</th>
                        <th>Benchmarks</th>
                        <th>Days</th>
                        <th>Loaded</th>
                        <th>Loaded by</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.files.map((f) => (
                        <tr key={f.id}>
                          <td>{f.filename}</td>
                          <td className="limit">{f.row_count}</td>
                          <td>{f.benchmarks.join(', ')}</td>
                          <td>{f.days.join(', ')}</td>
                          <td>{new Date(f.loaded_at).toLocaleString()}</td>
                          <td>{f.loaded_by ?? '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  )
}
